import * as React from 'react';
import { Field, FieldProps } from 'formik';
import { useIntl } from 'react-intl';
import Error from './Error';
import { ISimpleSelectMultipleOption } from '../../../interfaces/form';
import SearchService from '../../../Services/SearchService';

const usePrevious = (value: string) => {
  const ref: any = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current ? JSON.parse(ref.current) : [];
};

interface ISimpleSelectMultipleProps {
  id: string;
  name: string;
  label: string;
  options: ISimpleSelectMultipleOption[];
  placeholder: string;
  searchPlaceholder: string;
  width?: string;
  required?: boolean;
  onFocusCallback?: () => void;
  newStyle?: boolean;
  isCheckButton?: boolean;
  tooltip?: string;
}

const SimpleSelectMultiple: React.FC<ISimpleSelectMultipleProps> = (props) => {
  const { formatMessage: f } = useIntl();

  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = React.useState<boolean>(false);
  const [searchString, setSearchString] = React.useState<string>('');
  const [options, setOptions] = React.useState<ISimpleSelectMultipleOption[]>(props.options);

  const selectWidth = props.width ? props.width : 'col-sm-9';
  let errBorder = '';

  const handleShowDropdownMenu = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (isDropdownMenuOpen) {
      setIsDropdownMenuOpen(false);
    } else {
      setIsDropdownMenuOpen(true);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.currentTarget.value);
    const newArrSearch = props.options.filter((option: ISimpleSelectMultipleOption) =>
      SearchService.removeAccent(String(option.item).toLowerCase()).includes(
        SearchService.removeAccent(event.currentTarget.value.toLowerCase())
      )
    );
    if (event.currentTarget.value) {
      setOptions(newArrSearch);
    } else {
      setOptions(props.options);
    }
  };

  const handleClickOutside = React.useCallback(
    (event: any) => {
      const { id } = event.target;
      let checkboxId = '';
      const idArr = id.split('-');
      if (idArr.length > 0) {
        checkboxId = idArr[0];
      }
      let eventParentElement;
      if (event.target.parentElement) {
        eventParentElement = event.target.parentElement.className;
      }

      if (
        id === props.name ||
        id === 'check' ||
        id === 'uncheck' ||
        id === `selectMultipleSearch-${props.name}` ||
        checkboxId === `${props.name}_selectCheckbox` ||
        eventParentElement === `multipleCheckboxes-${props.name}` ||
        id === `icon-${props.name}`
      ) {
        setIsDropdownMenuOpen(true);
      } else {
        setIsDropdownMenuOpen(false);
      }
    },
    [isDropdownMenuOpen]
  );

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        if (isDropdownMenuOpen) {
          setIsDropdownMenuOpen(false);
        }
      }
    },
    [isDropdownMenuOpen]
  );

  React.useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClickOutside]);

  const prevOptions = usePrevious(JSON.stringify(props.options) || '');

  React.useEffect(() => {
    if (JSON.stringify(props.options) !== JSON.stringify(prevOptions)) {
      setOptions(props.options);
    }
  });

  return (
    <Field name={props.name} id={props.id}>
      {({ field: { value }, form: { setFieldValue }, meta: { error, touched } }: FieldProps) => {
        // Set error border for field
        if (touched && error) {
          errBorder = 'err-border';
        }

        const fieldValue = value || [];

        // Number of selected items showed in input field
        const numberOfSelectedItems = fieldValue.length;

        const handleOnChange = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
          event.preventDefault();
          const selectedValue = event.currentTarget.getAttribute('data-value');
          if (selectedValue) {
            if (fieldValue.includes(selectedValue)) {
              const newSelectedItems: string[] = fieldValue.filter(
                (item: string) => item !== selectedValue
              );
              // setSelectedItems(newSelectedItems);
              setFieldValue(props.name, newSelectedItems);
            } else {
              const newSelectedItems = fieldValue;
              newSelectedItems.push(selectedValue);
              // setSelectedItems(newSelectedItems);
              setFieldValue(props.name, newSelectedItems);
            }
          }
        };

        const check = (e: any) => {
          e.preventDefault();
          const ids = props.options.map((item: any) => {
            return String(item.value);
          });
          setFieldValue(props.name, ids);
        };

        const uncheck = (e: any) => {
          e.preventDefault();
          setFieldValue(props.name, []);
        };

        const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
          if (props.onFocusCallback) {
            props.onFocusCallback();
          }
        };

        const getPlaceholder = () => {
          let placeholder = '';
          if (numberOfSelectedItems === 1) {
            if (props.options) {
              const option = props.options.find((i) => String(i.value) === String(value));
              if (option && option.item) {
                placeholder = option.item;
              }
            }
          } else if (numberOfSelectedItems === 0) {
            placeholder = `${props.placeholder}`;
          } else {
            placeholder = `${f({ id: 'table.chosen' })}: ${numberOfSelectedItems}`;
          }
          return placeholder;
        };

        return !props.newStyle ? (
          <div>
            <div className="form-group">
              <div className="text-left font-weight-bold col-sm-3 control-label">
                {props.required && (
                  <span className="text-danger text-danger required-field-info">* </span>
                )}
                {props.label}
              </div>
              <div className={`${selectWidth} dropdown d-block e-dropdown-select open}`}>
                <div className={errBorder} style={{ position: 'relative' }}>
                  <input
                    onClick={handleShowDropdownMenu}
                    data-cy={`simple-select-multiple-${props.name}`}
                    type="text"
                    className="form-control font-weight-normal fs-2"
                    value={getPlaceholder()}
                    id={props.name}
                    readOnly={true}
                    onFocus={handleOnFocus}
                  />
                  <i
                    onClick={handleShowDropdownMenu}
                    data-cy={`simple-select-icon-${props.name}`}
                    id={`icon-${props.name}`}
                    className="cmd-icons cmd-icon-dropdown mr-1 fs-5 cursor-pointer"
                    style={{ position: 'absolute', right: '3px', top: '7px' }}
                  />{' '}
                </div>
                {isDropdownMenuOpen && (
                  <div
                    className="drop-menu-form-active dropdown-menu drop-menu-form dropdown-menu-custom fs-2 js-dropdown-keep-open"
                    aria-labelledby="dropdownMenu10"
                  >
                    <div className="pt-1 pb-2 px-2 border-bottom-on">
                      <input
                        type="text"
                        data-cy={`simple-select-multiple-search-${props.name}`}
                        className="form-control font-weight-normal fs-2"
                        id={`selectMultipleSearch-${props.name}`}
                        placeholder={props.searchPlaceholder}
                        onChange={handleSearch}
                        value={searchString}
                        autoFocus={true}
                      />
                    </div>

                    <div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {options && Object.values(options).length > 0 ? (
                          Object.values(options).map(
                            (item: ISimpleSelectMultipleOption, key: number) => (
                              <div
                                id={`${props.name}_selectCheckbox-div`}
                                key={key}
                                className={`e-checkbox px-2 py-1 d-block text-black  font-weight-normal ${
                                  key % 2 !== 0 ? 'bg-gray-light' : ''
                                }`}
                              >
                                <label
                                  onClick={handleOnChange}
                                  data-cy={`multipleCheckboxes-${item.value}`}
                                  className={`multipleCheckboxes-${props.name}`}
                                  data-value={item.value}
                                  style={{ width: '100%' }}
                                >
                                  <input
                                    type="checkbox"
                                    id={`selectCheckbox-${key}`}
                                    className={value.includes(String(item.value)) ? 'checked' : ''}
                                  />
                                  <span className="pl-5"> {Object.values(options)[key].item}</span>
                                </label>
                              </div>
                            )
                          )
                        ) : (
                          <div>&nbsp;...</div>
                        )}
                      </div>
                    </div>

                    {props.isCheckButton && (
                      <div
                        className="row m-0 px-1 py-1 pt-2"
                        style={{ borderTop: '1px solid #ddd' }}
                      >
                        <div className="col-6 px-xs">
                          <a
                            href=""
                            id="check"
                            onClick={check}
                            className="e-button d-flex"
                          >
                            {f({ id: 'reports.check' })}
                          </a>
                        </div>
                        <div className="col-6 px-xs">
                          <a
                            href=""
                            id="uncheck"
                            onClick={uncheck}
                            className="e-button d-flex"
                          >
                            {f({ id: 'reports.uncheck' })}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {touched && error && <Error message={error} newStyle={props.newStyle} />}
              </div>
            </div>
          </div>
        ) : (
          <div className={`form-group false ${touched && error && 'form-group-error'}`}>
            <div className="row">
              <div className="col-12 col-lg-3 form-label">
                {props.required && (
                  <span className="text-danger text-danger required-field-info">* </span>
                )}
                {props.label}
              </div>
              <div className="col-12 col-lg-9">
                <div className="b-dropdown">
                  <div style={{ position: 'relative' }}>
                    <div
                      className="form-control-labels"
                      onClick={handleShowDropdownMenu}
                      data-cy={`simple-select-multiple-${props.name}`}
                      id={props.name}
                      onFocus={handleOnFocus}
                      title={props.tooltip || ''}
                    >
                      {getPlaceholder()}
                    </div>
                  </div>
                  {isDropdownMenuOpen && (
                    <div className="b-dropdown__content" aria-labelledby="dropdownMenu10">
                      <div className="pt-1 pb-2 px-2 border-bottom-on">
                        <input
                          type="text"
                          data-cy={`simple-select-multiple-search-${props.name}`}
                          className="form-control font-weight-normal fs-2"
                          id={`selectMultipleSearch-${props.name}`}
                          placeholder={props.searchPlaceholder}
                          onChange={handleSearch}
                          value={searchString}
                          autoFocus={true}
                          style={{ borderColor: '#ccc', background: '#fff' }}
                        />
                      </div>

                      <div>
                        <div
                          className="checkbox-group-component form-group"
                          style={{ maxHeight: '200px', overflowY: 'auto', width: '100%' }}
                        >
                          {options && Object.values(options).length > 0 ? (
                            Object.values(options).map(
                              (item: ISimpleSelectMultipleOption, key: number) => (
                                <div
                                  id={`${props.name}_selectCheckbox-div`}
                                  key={key}
                                  style={{ marginLeft: '0', padding: '8px 12px', color: '#494949' }}
                                  className={`e-checkbox d-block text-black  font-weight-normal ${
                                    key % 2 !== 0 ? 'bg-gray-lighter' : ''
                                  }`}
                                >
                                  <label
                                    onClick={handleOnChange}
                                    data-cy={`multipleCheckboxes-${item.value}`}
                                    className={`multipleCheckboxes-${props.name}`}
                                    data-value={item.value}
                                    style={{ width: '100%' }}
                                  >
                                    <input
                                      type="checkbox"
                                      id={`selectCheckbox-${key}`}
                                      className={
                                        value.includes(String(item.value)) ? 'checked' : ''
                                      }
                                      checked={!!value.includes(String(item.value))}
                                    />
                                    <span className="pl-5">
                                      &nbsp;{Object.values(options)[key].item}
                                    </span>
                                  </label>
                                </div>
                              )
                            )
                          ) : (
                            <div>&nbsp;...</div>
                          )}
                        </div>
                      </div>

                      {props.isCheckButton && (
                        <div
                          className="row m-0 px-1 py-1 pt-2"
                          style={{ borderTop: '1px solid #ddd' }}
                        >
                          <div className="col-6 px-xs">
                            <a
                              href=""
                              id="check"
                              onClick={check}
                              className="e-button d-flex"
                            >
                              {f({ id: 'reports.check' })}
                            </a>
                          </div>
                          <div className="col-6 px-xs">
                            <a
                              href=""
                              id="uncheck"
                              onClick={uncheck}
                              className="e-button d-flex"
                            >
                              {f({ id: 'reports.uncheck' })}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {touched && error && <Error message={error} newStyle={true} />}
              </div>
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export default SimpleSelectMultiple;
